interface IHeaderOneDisplayWord {
    text: string,
    highlighted: boolean,
}

export interface IHeaderOneDisplaySegments {
    primaryWords: IHeaderOneDisplayWord[],
    secondaryText: string | null,
    centerAlign: boolean,
}

const HEADER_ONE_CENTER_ALIGN_WORD_LIMIT = 10;
const HEADER_ONE_STOP_WORDS = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'by',
    'for',
    'from',
    'in',
    'of',
    'on',
    'or',
    'the',
    'to',
    'with',
]);

/**
 * Normalizes header text before the display heuristic splits it into a large
 * primary lockup and a smaller trailing lockup.
 */
function normalizeHeaderOneText(headingText: string): string {
    return headingText.replace(/\s+/g, ' ').trim();
}

/**
 * Biases the split slightly toward the smaller trailing lockup so odd-length
 * headings do not leave too much weight in the dominant front lockup.
 */
function getHeaderOneSecondaryWordCount(words: string[]): number {
    if (words.length <= 1) {
        return 0;
    }

    return Math.min(words.length - 1, Math.ceil(words.length / 2));
}

/**
 * Removes surrounding punctuation so the highlight heuristic can look at the
 * actual word content.
 */
function getHeaderOneComparableWord(word: string): string {
    return word
        .replace(/^[^a-z0-9]+/i, '')
        .replace(/[^a-z0-9]+$/i, '')
        .toLowerCase();
}

/**
 * Picks a single word to accent. The heuristic prefers substantive,
 * longer words and falls back to the longest remaining token when needed.
 */
function getHeaderOneHighlightIndex(primaryWords: string[]): number | null {
    if (primaryWords.length <= 0) {
        return null;
    }

    const middleIndex = (primaryWords.length - 1) / 2;
    const comparableWords = primaryWords.map((word, index) => ({
        index,
        comparableWord: getHeaderOneComparableWord(word),
    })).filter(({ comparableWord }) => comparableWord.length > 0);

    if (comparableWords.length <= 0) {
        return null;
    }

    const substantiveWords = comparableWords.filter(({ comparableWord }) => (
        comparableWord.length > 1
        && !HEADER_ONE_STOP_WORDS.has(comparableWord)
    ));
    const candidateWords = substantiveWords.length > 0
        ? substantiveWords
        : comparableWords;

    const sortedCandidates = [...candidateWords].sort((firstWord, secondWord) => {
        if (secondWord.comparableWord.length !== firstWord.comparableWord.length) {
            return secondWord.comparableWord.length - firstWord.comparableWord.length;
        }

        return Math.abs(firstWord.index - middleIndex) - Math.abs(secondWord.index - middleIndex);
    });

    return sortedCandidates[0]?.index ?? null;
}

/**
 * Builds a read-only display model for HeaderOne. Short headings still use the
 * split lockup, but they reuse the centered mobile alignment on wide viewports.
 */
export function buildHeaderOneDisplaySegments(headingText: string): IHeaderOneDisplaySegments {
    const normalizedHeadingText = normalizeHeaderOneText(headingText);

    if (!normalizedHeadingText) {
        return {
            primaryWords: [],
            secondaryText: null,
            centerAlign: false,
        };
    }

    const words = normalizedHeadingText.split(' ');
    const centerAlign = words.length < HEADER_ONE_CENTER_ALIGN_WORD_LIMIT;

    if (words.length === 1) {
        return {
            primaryWords: [{
                text: words[0],
                highlighted: false,
            }],
            secondaryText: null,
            centerAlign,
        };
    }

    const secondaryWordCount = getHeaderOneSecondaryWordCount(words);
    const primaryWords = words.slice(0, words.length - secondaryWordCount);
    const secondaryWords = words.slice(words.length - secondaryWordCount);
    const highlightIndex = getHeaderOneHighlightIndex(primaryWords);

    return {
        primaryWords: primaryWords.map((word, index) => ({
            text: word,
            highlighted: highlightIndex === index,
        })),
        secondaryText: secondaryWords.join(' '),
        centerAlign,
    };
}
